import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Tabs,
  Tab
} from '@mui/material';
import { Save, Refresh, History, FlightTakeoff, FlightLand } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { hasSuperAdminView } from '../../utils/rolePermissions';
import { useDgcaBaselineData } from './DgcaBaselineManagement/hooks/useDgcaBaselineData';
import { useDgcaBaselineHandlers } from './DgcaBaselineManagement/hooks/useDgcaBaselineHandlers';
import DgcaBaselineHistory from './DgcaBaselineManagement/dialogs/DgcaBaselineHistory';
import ResetDialog from './DgcaBaselineManagement/dialogs/ResetDialog';
import DutyTimeFDPRules from './DgcaBaselineManagement/sections/DutyTimeFDPRules';
import HelicopterFDPRules from './DgcaBaselineManagement/sections/HelicopterFDPRules';
import FixedWingTwoPilot from './DgcaBaselineManagement/sections/FixedWingTwoPilot';
import FixedWingAugmentedCrew from './DgcaBaselineManagement/sections/FixedWingAugmentedCrew';
import FixedWingSinglePilot from './DgcaBaselineManagement/sections/FixedWingSinglePilot';
import FixedWingCumulativeLimits from './DgcaBaselineManagement/sections/FixedWingCumulativeLimits';
import FixedWingSplitDuty from './DgcaBaselineManagement/sections/FixedWingSplitDuty';
import FixedWingWOCL from './DgcaBaselineManagement/sections/FixedWingWOCL';
import RestPeriodRules from './DgcaBaselineManagement/sections/RestPeriodRules';
import CumulativeFlightTimeLimits from './DgcaBaselineManagement/sections/CumulativeFlightTimeLimits';
import WeeklyRestRules from './DgcaBaselineManagement/sections/WeeklyRestRules';
import StandbyReserveRules from './DgcaBaselineManagement/sections/StandbyReserveRules';
import RosterPublication from './DgcaBaselineManagement/sections/RosterPublication';
import RecordKeeping from './DgcaBaselineManagement/sections/RecordKeeping';
import FatigueReporting from './DgcaBaselineManagement/sections/FatigueReporting';
import Training from './DgcaBaselineManagement/sections/Training';

const DgcaBaselineManagement = () => {
  const { user, isReadOnly } = useAuth();
  const [aircraftType, setAircraftType] = useState('Fixed Wing');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const {
    baseline,
    setBaseline,
    isCustom,
    history,
    loading,
    historyLoading,
    error,
    setError,
    fetchBaseline,
    fetchHistory
  } = useDgcaBaselineData(aircraftType);

  const {
    saving,
    success,
    setSuccess,
    resetDialogOpen,
    setResetDialogOpen,
    handleChange,
    handleSave,
    handleReset
  } = useDgcaBaselineHandlers(baseline, setBaseline, aircraftType, fetchBaseline, fetchHistory);

  const handleAircraftTypeChange = (event, newValue) => {
    setAircraftType(newValue);
  };

  if (!user || !hasSuperAdminView(user.role)) {
    return (
      <Box>
        <Alert severity="error">Access denied. Only SUPER_ADMIN can manage baseline configuration.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4">DGCA Baseline Configuration</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Configure the baseline DGCA regulatory limits by aircraft type. These values serve as the safety floor that cannot be undercut by configurable DGCA Profiles.
        </Typography>
        
        {/* Aircraft Type Selector */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <Tabs 
            value={aircraftType} 
            onChange={handleAircraftTypeChange}
            aria-label="aircraft type tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              icon={<FlightTakeoff />} 
              iconPosition="start"
              label="Fixed Wing" 
              value="Fixed Wing"
              sx={{ textTransform: 'none', minHeight: 64 }}
            />
            <Tab 
              icon={<FlightLand />} 
              iconPosition="start"
              label="Helicopter" 
              value="Helicopter"
              sx={{ textTransform: 'none', minHeight: 64 }}
            />
          </Tabs>
        </Box>

        {isCustom && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Custom baseline configuration for <strong>{aircraftType}</strong> is active. Any active DGCA profile must be equal to or more restrictive than these values.
          </Alert>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Warning:</strong> Changing baseline values for <strong>{aircraftType}</strong> will affect all future DGCA profile validations for this aircraft type. 
          Ensure changes comply with {aircraftType === 'Fixed Wing' ? 'D7J-J4-Fixed Wing.pdf' : 'D7J-J2 - Helicopter.pdf'} and DGCA CAR Section 3, Series C and CAR Section 7, Series J (FDTL) regulations.
        </Typography>
      </Alert>

      {/* Common Sections */}
      <DutyTimeFDPRules baseline={baseline} handleChange={handleChange} />

      {/* Helicopter-Specific Sections */}
      {aircraftType === 'Helicopter' && (
        <HelicopterFDPRules baseline={baseline} handleChange={handleChange} />
      )}

      {/* Fixed Wing-Specific Sections */}
      {aircraftType === 'Fixed Wing' && (
        <>
          <FixedWingTwoPilot baseline={baseline} handleChange={handleChange} />
          <FixedWingAugmentedCrew baseline={baseline} handleChange={handleChange} />
          <FixedWingSinglePilot baseline={baseline} handleChange={handleChange} />
          <FixedWingCumulativeLimits baseline={baseline} handleChange={handleChange} />
          <FixedWingSplitDuty baseline={baseline} handleChange={handleChange} />
          <FixedWingWOCL baseline={baseline} handleChange={handleChange} />
        </>
      )}

      {/* Common Sections */}
      <RestPeriodRules baseline={baseline} handleChange={handleChange} />
      <CumulativeFlightTimeLimits baseline={baseline} handleChange={handleChange} aircraftType={aircraftType} />
      <WeeklyRestRules baseline={baseline} handleChange={handleChange} />
      <StandbyReserveRules baseline={baseline} handleChange={handleChange} />
      <RosterPublication baseline={baseline} handleChange={handleChange} />
      <RecordKeeping baseline={baseline} handleChange={handleChange} />
      <FatigueReporting baseline={baseline} handleChange={handleChange} />
      <Training baseline={baseline} handleChange={handleChange} />

      <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<History />}
          onClick={() => setHistoryDialogOpen(true)}
        >
          View History
        </Button>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchBaseline}
          disabled={saving}
        >
          Reset Changes
        </Button>
        {isCustom && !isReadOnly && (
          <Button
            variant="outlined"
            color="warning"
            onClick={() => setResetDialogOpen(true)}
            disabled={saving}
          >
            Reset to Defaults
          </Button>
        )}
        {!isReadOnly && (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Baseline'}
          </Button>
        )}
      </Box>

      {/* Dialogs */}
      <DgcaBaselineHistory
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        history={history}
        loading={historyLoading}
      />

      <ResetDialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onConfirm={handleReset}
        aircraftType={aircraftType}
        saving={saving}
      />
    </Box>
  );
};

export default DgcaBaselineManagement;
